import { Element } from '@vindr/services/apis/element.api';
import { Omit, addToArray } from '@vindr/utils/helpers/common.helper';
import { BaseElement, BaseElementProps } from '../base';
import { ElementFactory } from '../base/ElementFactory';
import { StructuredReportElement } from '../types';
import InputElement from '../input/InputElement';

export interface GenerateListElementProps
  extends Omit<BaseElementProps, 'value' | 'default_value'> {
  parent_label?: string;
  value?: number;
  label?: string;
  placeholder?: string;
  item_label?: string;
  is_show_item_label?: boolean;
  is_editable_detail_value?: boolean;
  default_item_value?: string;
  item_values?: (string | Element)[];
  default_value: number;
}

class GenerateListElement extends BaseElement {
  public value: number;
  public default_value: number;
  public label: string;
  public is_show_label: boolean;
  public is_editable_value: boolean;
  public is_editable_detail_value: boolean;
  public placeholder: string;
  public detail_label: string;
  public is_show_detail_label: boolean;
  public detail_value: string;
  public item_values: (string | StructuredReportElement)[];
  public shouldHighlightIndex; // mark the item should be highlight, if == -1, highlight all element
  public elements: StructuredReportElement[]; // linked element, should only one item

  constructor(props: GenerateListElementProps) {
    super(props);
    this.level = (props.parent?.level ?? -1) + 1;

    const {
      label,
      is_show_label,
      placeholder,
      value,
      generated_number,
      is_editable_value,
      is_editable_detail_value,
      detail_label,
      is_show_detail_label,
      detail_value,
      item_values,
    } = props;

    // this.id = uuidv4();
    this.value = value ?? generated_number ?? (item_values?.length || 0);
    this.label = label;
    this.is_show_label = is_show_label;
    this.is_editable_value = is_editable_value;
    this.placeholder = placeholder;
    this.detail_label = detail_label || '';
    this.is_show_detail_label = is_show_detail_label;
    this.is_editable_detail_value = is_editable_detail_value;
    this.detail_value = detail_value || '';
    this.shouldHighlightIndex = -1;
    this.item_values = [];
    this.linked_element_ids = props.linked_element_ids;
    this.elements = Array.isArray(props.elements)
      ? props.elements.map((element) =>
          ElementFactory.createElement({
            ...element,
            parent: this,
            level: this.level + 1,
          })
        )
      : [];
    if (item_values) {
      this.item_values = item_values.map((it) => {
        if (typeof it == 'string') {
          return it;
        } else {
          return ElementFactory.createElement({ ...it, parent: this, level: this.level + 1 });
        }
      });
    } else {
      this.initializeItems();
    }
  }

  public setValue(length: number) {
    // value should be the number of items to be generated
    const currentLength = this.item_values.length;
    if (currentLength !== length) {
      this.shouldHighlightIndex = -1;
    }

    for (let i = 0; i < length; i++) {
      if (this.item_values[i] === undefined) {
        this.item_values[i] = this.createItem();
      }
    }
    if (currentLength > length) {
      this.item_values = this.item_values.slice(0, length);
    } else if (currentLength < length) {
      const itemsToAdd = length - currentLength;
      const newItems = Array.from({ length: itemsToAdd }, () => this.createItem());
      this.item_values.push(...newItems);
    }
    this.value = length;
    return this.item_values;
  }

  public updateChildValue(value: string, index: number) {
    if (this.item_values[index] instanceof BaseElement) {
      (this.item_values[index] as InputElement).setValue(value);
    } else {
      this.item_values[index] = value;
    }
    this.shouldHighlightIndex = index;
    return this.item_values;
  }

  public setElements(elements: Element[]) {
    this.elements = addToArray(elements).map((val) =>
      ElementFactory.createElement({ ...val, parent: this, level: this.level + 1 })
    );
    if (!this.item_values?.length) {
      this.initializeItems(); // Update item_values based on the new elements
    }
    return this;
  }

  public setHighlight(val: boolean): void {
    this.highlight = val;
    // If need hightlight, just need highlight this, else should dehighlight every childrens
    if (this.item_values && this.item_values[0] instanceof BaseElement) {
      for (const child of this.item_values) {
        if (child instanceof BaseElement) {
          child.setHighlight(val);
        }
      }
    }
  }

  private createItem(): string | StructuredReportElement {
    if (this.elements.length === 0 && !this.linked_element_ids?.length) {
      // the value should be detail_value (string)
      return this.detail_value;
    }

    if (this.elements.length > 0) {
      // the value should be a linked element
      const element = this.elements[0];

      return ElementFactory.createElement({
        ...element,
        parent: this,
        level: this.level + 1,
      });
    }
  }

  private initializeItems() {
    const generatedNumber = this.value;

    if (!Number.isInteger(generatedNumber) || generatedNumber < 0) {
      // console.error('Invalid generatedNumber:', generatedNumber);
      // this.item_values = [];
      // return;
    }

    if (this.linked_element_ids?.length && !this.elements?.length) {
      // this element has link but the link still not be loaded
      this.item_values = [];
    } else {
      this.item_values = Array.from({ length: generatedNumber }, () => this.createItem());
    }
  }

  public render(): string {
    if (this.is_hidden) return '';
    const value = Array.isArray(this.value) ? this.value?.length : Number(this.value);

    const header = this.is_show_label ? `${this.label}: ${value}` : `${value}`;
    const listItemValues = this.item_values.slice(0, this.value);
    const body = listItemValues?.length
      ? `<div style="display: flex; flex-direction: column;">${listItemValues
          .map(
            (item: GenerateListElement['item_values']['0'], idx) =>
              `<div data-name="${this.name}" class="list-item" style='margin-left: 8px'>${
                this.is_show_detail_label ? `${this.detail_label} ${idx + 1}` : ''
              }${
                typeof item === 'string'
                  ? this.shouldHighlightIndex === idx
                    ? this.withHighlight(item)
                    : item
                  : item
                  ? item.render()
                  : ''
              }</div>`
          )
          .join('')}</div>`
      : '';

    const result = `<div role="list" data-name="${this.name}" style="display: flex; flex-direction: column"><span>${header}</span>${body}</div>`;
    return this.shouldHighlightIndex < 0 ? this.withHighlight(result) : result;
  }

  public toJSON() {
    const json = Omit(this, ['parent', 'props', 'highlight']);
    json.elements = (this.elements || [])?.map((child) =>
      typeof child?.toJSON === 'function' ? child.toJSON() : child
    );
    return json;
  }
}

export default GenerateListElement;
